/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { BeakerIcon, CircleStackIcon, RocketLaunchIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { managedServiceApi } from '../../services/api';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { computeMonthlyTotal } from './pricing';
import {
  ManagedHeader,
  PRODUCT_NAMES,
  PRODUCT_ORDER,
  ManagedStatusTag,
  ReorderState,
  useManagedInstances,
} from '../../components/managed/managedShared';
import OrderServiceModal, { formatMoney } from '../../components/managed/OrderServiceModal';
import {
  Card,
  Button,
  Tag,
  SectionHeader,
  TableCard,
  EmptyState,
  Skeleton,
  Page,
} from '../../components/console/ui';
import type { CatalogOffering, ManagedServiceProduct, ManagedServiceTier } from '../../types/managedServices';

interface ProductMeta {
  name: string;
  tagline: string;
  features: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// Static per-product metadata that the catalog API doesn't carry (icon, a
// short tagline, feature bullets, and a fallback display name for products
// with no active offerings yet - i.e. "Coming soon" cards). Display order is
// fixed in PRODUCT_ORDER. When a product HAS active offerings, its display
// name is derived from the catalog row (see deriveProductName).
const PRODUCT_META: Record<ManagedServiceProduct, ProductMeta> = {
  mlflow: {
    name: 'MLflow',
    tagline: 'Experiment tracking',
    features: ['Runs, metrics and artifacts', 'Model registry'],
    icon: BeakerIcon,
  },
  kfp: {
    name: 'Kubeflow Pipelines',
    tagline: 'Pipeline orchestration',
    features: ['KFP UI for runs and DAGs', 'Scheduled pipelines'],
    icon: Squares2X2Icon,
  },
  dvc: {
    name: 'DVC Remote',
    tagline: 'Data versioning',
    features: ['S3-compatible remote', 'Web browser for versions'],
    icon: CircleStackIcon,
  },
  metaflow: {
    name: 'Metaflow',
    tagline: 'Workflow metadata',
    features: ['Metadata service', 'Metaflow UI'],
    icon: RocketLaunchIcon,
  },
};

// Offering.display_name is seeded as "<Product name> - <Tier label>" (e.g.
// "MLflow - Small"). Strip the tier suffix to recover the product's
// admin-configured display name straight from the catalog row.
const deriveProductName = (offering: CatalogOffering): string => offering.display_name.split(' - ')[0].trim();

const ManagedMLOpsCatalog: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { autoSelectFirstProject } = useSelectedProject();

  useEffect(() => {
    autoSelectFirstProject();
  }, [autoSelectFirstProject]);

  const [ordering, setOrdering] = useState<{
    product: ManagedServiceProduct;
    initial?: { tier?: ManagedServiceTier; storage_gb?: number; name?: string };
  } | null>(null);

  const { data: catalog, isLoading, isError, refetch } = useQuery({
    queryKey: ['managed-services-catalog'],
    queryFn: () => managedServiceApi.getCatalog().then((res) => res.data as CatalogOffering[]),
  });

  const { data: instances } = useManagedInstances();
  const pastInstances = (instances || []).filter((i) => i.status === 'deleted');

  const productCards = useMemo(() => {
    const map = new Map<ManagedServiceProduct, CatalogOffering[]>();
    (catalog || []).forEach((o) => {
      const list = map.get(o.product) || [];
      list.push(o);
      map.set(o.product, list);
    });
    return PRODUCT_ORDER.map((product) => {
      const offerings = (map.get(product) || []).slice().sort((a, b) => a.monthly_price - b.monthly_price);
      const meta = PRODUCT_META[product];
      return {
        product,
        offerings,
        orderable: offerings.length > 0,
        name: offerings.length > 0 ? deriveProductName(offerings[0]) : meta.name,
        meta,
      };
    });
  }, [catalog]);

  // "Re-order" from My instances arrives with router state; open the dialog once.
  useEffect(() => {
    const st = location.state as ReorderState | null;
    if (!st?.reorder || !catalog) return;
    setOrdering({ product: st.reorder.product, initial: st.reorder });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, catalog, navigate]);

  const orderingCard = ordering ? productCards.find((c) => c.product === ordering.product) : undefined;

  return (
    <Page>
      <ManagedHeader subtitle="Provisioned, patched and billed for you. Each service gets its own web UI and endpoint." />

      {isLoading ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,270px),1fr))]">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[210px]" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn’t load the catalog"
          body="Failed to load the managed services catalog."
          actions={<Button onClick={() => refetch()}>Retry</Button>}
        />
      ) : (catalog || []).length === 0 ? (
        <EmptyState
          title="No offerings available"
          body="There are no active managed service offerings for your account right now. Please check back later."
        />
      ) : (
        <section
          aria-label="Catalog"
          className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,270px),1fr))]"
        >
          {productCards.map((p) => {
            const Icon = p.meta.icon;
            const cheapest = p.offerings[0];
            return (
              <Card as="section" key={p.product} className="p-5 flex flex-col gap-3.5" aria-labelledby={`mp-${p.product}`}>
                <div className="flex gap-3 items-center">
                  <span className="ps-icb">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <h2 id={`mp-${p.product}`} className="m-0 text-[17px] font-medium flex items-center gap-2">
                      {p.name}
                      {!p.orderable && <Tag tone="off">Coming soon</Tag>}
                    </h2>
                    <div className="text-[13px] text-ps-muted">{p.meta.tagline}</div>
                  </div>
                </div>
                <ul className="m-0 p-0 list-none flex flex-col gap-1.5 text-sm text-ps-muted">
                  {p.meta.features.map((f) => (
                    <li key={f} className="flex gap-2 items-start">
                      <span className="text-ps-accent" aria-hidden="true">
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-3 border-t border-ps-raised flex justify-between items-center gap-2">
                  <div>
                    {cheapest ? (
                      <>
                        <div className="font-semibold">
                          {p.offerings.length > 1 && <span className="text-xs font-normal text-ps-muted">from </span>}
                          {formatMoney(computeMonthlyTotal(cheapest, cheapest.min_storage_gb), cheapest.currency).replace(
                            /\.00$/,
                            '',
                          )}
                          <span className="text-ps-muted font-normal text-[13px]">/mo</span>
                        </div>
                        <div className="text-xs text-ps-subtle capitalize">
                          {cheapest.tier} · {cheapest.min_storage_gb} GB
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-ps-subtle">Not yet available</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    disabled={!p.orderable}
                    onClick={() => setOrdering({ product: p.product })}
                    aria-label={`Order ${p.name}`}
                  >
                    Order
                  </Button>
                </div>
              </Card>
            );
          })}
        </section>
      )}

      {pastInstances.length > 0 && (
        <section className="flex flex-col gap-3" aria-labelledby="past-inst">
          <SectionHeader
            id="past-inst"
            title="Past instances"
            action={<span className="text-[13px] text-ps-subtle">Deleted instances stop billing at the next renewal</span>}
          />
          <TableCard>
            <thead>
              <tr>
                <th>Name</th>
                <th>Service · tier</th>
                <th>Status</th>
                <th>Monthly</th>
                <th>Was due</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pastInstances.map((i) => (
                <tr key={i.id}>
                  <td>
                    <div className="text-ps-text">{i.name}</div>
                    <div className="text-xs text-ps-subtle">{i.storage_gb} GB storage</div>
                  </td>
                  <td>
                    {PRODUCT_NAMES[i.product] || i.product} · <span className="capitalize">{i.tier}</span>
                  </td>
                  <td>
                    <ManagedStatusTag status={i.status} />
                  </td>
                  <td>{formatMoney(i.monthly_cost + i.storage_monthly_cost, i.currency)}</td>
                  <td className="text-ps-muted">
                    {i.next_renewal_at ? format(new Date(i.next_renewal_at), 'd MMM yyyy') : '-'}
                  </td>
                  <td className="text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!productCards.find((c) => c.product === i.product)?.orderable}
                      onClick={() =>
                        setOrdering({
                          product: i.product,
                          initial: { tier: i.tier, storage_gb: i.storage_gb, name: i.name },
                        })
                      }
                    >
                      Re-order
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </section>
      )}

      {ordering && orderingCard && orderingCard.orderable && (
        <OrderServiceModal
          productName={orderingCard.name}
          offerings={orderingCard.offerings}
          initial={ordering.initial}
          onClose={() => setOrdering(null)}
        />
      )}
    </Page>
  );
};

export default ManagedMLOpsCatalog;
